import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

/**
 * Reusable in-app Guide pane. Use inside a <TabsContent value="guide"> block.
 *
 * Props:
 *  - title            (string) module display name, e.g. "AI Chat Assistant"
 *  - whatItIs         (string) 2-3 sentence plain description
 *  - capabilities     (array of {title, body}) — 4-5 use cases
 *  - howTo            (array of strings — supports markdown-ish **bold** + `code`)
 *  - ctaLabel         (string) e.g. "Got it — take me to AI Chat"
 *  - onCta            (function) handler — usually setTab("main")
 *  - extraNote        (string, optional) tiny line below CTA
 */
export function ModuleGuide({ title, whatItIs, capabilities = [], howTo = [], ctaLabel, onCta, extraNote }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-[720px] py-6"
      data-testid="module-guide"
    >
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
          Guide · {title}
        </div>
        <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-300" data-testid="guide-what">
          {whatItIs}
        </p>
      </header>

      {capabilities.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white" data-testid="guide-section-can-do">
            What it can do for you
          </h2>
          <ul className="mt-6 space-y-5">
            {capabilities.map((c, i) => (
              <li key={i} className="leading-relaxed" data-testid={`guide-capability-${i}`}>
                <div className="text-lg font-medium text-slate-900 dark:text-white">{c.title}</div>
                <div className="mt-1 text-base text-slate-600 dark:text-slate-400">{c.body}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {howTo.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white" data-testid="guide-section-how">
            How to use it
          </h2>
          <ol className="mt-6 list-decimal space-y-4 pl-6 text-base leading-relaxed text-slate-700 dark:text-slate-300">
            {howTo.map((step, i) => (
              <li key={i} data-testid={`guide-step-${i}`}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderInline(step), { ALLOWED_TAGS: ["strong", "code"], ALLOWED_ATTR: ["class"] }) }} />
              </li>
            ))}
          </ol>
        </section>
      )}

      {ctaLabel && (
        <div className="mb-3 mt-8">
          <Button
            onClick={onCta}
            className="rounded-full bg-indigo-600 px-6 text-white hover:bg-indigo-700"
            data-testid="guide-cta"
          >
            {ctaLabel}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      )}

      {extraNote && (
        <p className="text-sm text-slate-500" data-testid="guide-extra-note">{extraNote}</p>
      )}

      <p className="mt-6 text-sm text-slate-500" data-testid="guide-still-stuck">
        Still stuck? Email{" "}
        <a href="mailto:info@symplee.ai" className="text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-300">
          info@symplee.ai
        </a>
        {" "}— we're real humans and we'll help.
      </p>
    </motion.div>
  );
}

// Minimal inline renderer for **bold** and `code` (keeps guide copy readable in source).
function renderInline(s) {
  return s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900 dark:text-white">$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="rounded bg-slate-100 dark:bg-white/10 px-1 py-0.5 font-mono text-[0.9em] text-slate-800 dark:text-slate-200">$1</code>');
}
